import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import PropTypes from "prop-types";

import Modal from "../../../../parts/modal-new";
import NutrientsChart from "../../../athlete-portal/nutrition/components/NutrientsChart";
import KcalInputs from "../../../athlete-portal/nutrition/components/KcalInputs";
import ExtraPreferences from "../../../athlete-portal/nutrition/components/ExtraPreferences";
import Dropdown from "../../../../parts/dropdown";
import ToolTip from "../../../../parts/tooltip";
import SvgIcon from "../../../../parts/SvgIcon";
import { DIETARY_TYPES, CUISINE_OPTIONS } from "../../../../constants";
import { getDateWithDayToDisplay } from "../../../../utils/date-helpers";

const DIETARY_I18N = {
  Vegetarian: "vegetarian",
  "Gluten-free": "glutenFree",
  Vegan: "vegan",
};

const RegenerateMealPlanModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const [isLoading, setIsLoading] = useState(false);
  const mealPlan = locals?.mealPlan || {};
  const { query } = locals || {};
  const { mealPlan: mealPlanId, athlete, week: weekParam = 1 } = query || {};
  const weeks = Array.isArray(mealPlan.weeks) ? mealPlan.weeks : [];
  const weekIndex = Math.max(
    0,
    Math.min(weeks.length - 1, Number(weekParam) - 1)
  );
  const [cuisineSearch, setCuisineSearch] = useState("");
  const [showCuisineSuggestions, setShowCuisineSuggestions] = useState(false);
  const cuisineInputRef = useRef(null);
  const cuisineDropdownRef = useRef(null);

  const [form, setForm] = useState({
    weekIndex: weekIndex,
    dayIndex: 0,
    mealActions: [],
    calories: "",
    proteins: "",
    carbs: "",
    fats: "",
    fibers: "",
    proteinsPercent: "",
    carbsPercent: "",
    fatsPercent: "",
    extraPreferences: "",
    selectedPresets: [],
    effortLevel: 3,
    dietaryRestrictions: {
      allergies: [],
      dietType: "",
    },
    foodPreferences: {
      lovedCuisines: [],
      dislikedIngredients: [],
    },
  });

  const {
    weekIndex: selectedWeekIndex,
    dayIndex,
    mealActions,
    calories,
    proteins,
    carbs,
    fats,
    fibers,
    proteinsPercent,
    carbsPercent,
    fatsPercent,
    extraPreferences,
    selectedPresets,
    effortLevel,
    dietaryRestrictions,
    foodPreferences,
  } = form;

  const selectedWeek = useMemo(() => {
    return weeks[selectedWeekIndex] || {};
  }, [weeks, selectedWeekIndex]);

  const days = useMemo(() => {
    return selectedWeek.days || [];
  }, [selectedWeek]);

  const selectedDay = useMemo(() => {
    return days[dayIndex] || {};
  }, [days, dayIndex]);

  const meals = useMemo(() => {
    return selectedDay.meals || [];
  }, [selectedDay]);

  const presetPrompts = [
    "Balanced macros for maintenance",
    "High protein focus",
    "Lower carbs, higher fats",
    "Avoid ultra-processed foods",
    "Budget friendly ingredients",
  ];

  const weekOptions = weeks.map((_, index) => ({
    text: `Week ${index + 1}`,
    value: index,
  }));

  const dayOptions = days.map((day, index) => ({
    text: day.date ? getDateWithDayToDisplay(day.date) : `Day ${index + 1}`,
    value: index,
  }));

  const handleChange = (e, updates = null) => {
    e.persist?.();
    const { name, value, type: inputType, checked } = e.target || {};
    const val =
      inputType === "checkbox" ? Boolean(checked) : (value ?? "").toString();

    setForm((prev) => {
      const updatesObj = updates || { [name]: val };
      const newForm = { ...prev, ...updatesObj };

      // Reset mealActions when day changes
      if (name === "dayIndex" && prev.dayIndex !== Number(val)) {
        newForm.mealActions = [];
      }

      return newForm;
    });
  };

  const handleWeekSelect = (val) => {
    const newWeekIndex = Number(val);
    const newWeek = weeks[newWeekIndex] || {};
    const newDays = newWeek.days || [];
    const validDayIndex = Math.min(dayIndex, newDays.length - 1);

    setForm((prev) => ({
      ...prev,
      weekIndex: newWeekIndex,
      dayIndex: Math.max(0, validDayIndex),
      mealActions: [],
    }));
  };

  const handleDaySelect = (val) => {
    setForm((prev) => ({
      ...prev,
      dayIndex: Number(val),
      mealActions: [],
    }));
  };

  const handleMealAction = (mealIndex, action) => {
    setForm((prev) => {
      const existingAction = prev.mealActions.find(
        (ma) => ma.mealIndex === mealIndex
      );

      if (existingAction) {
        if (existingAction.action === action) {
          // Remove action if clicking the same action
          return {
            ...prev,
            mealActions: prev.mealActions.filter(
              (ma) => ma.mealIndex !== mealIndex
            ),
          };
        } else {
          // Update action
          return {
            ...prev,
            mealActions: prev.mealActions.map((ma) =>
              ma.mealIndex === mealIndex ? { mealIndex, action } : ma
            ),
          };
        }
      } else {
        // Add new action
        return {
          ...prev,
          mealActions: [...prev.mealActions, { mealIndex, action }],
        };
      }
    });
  };

  const getMealAction = (mealIndex) => {
    const action = mealActions.find((ma) => ma.mealIndex === mealIndex);
    return action ? action.action : null;
  };

  const handlePresetPrompt = (prompt) => {
    setForm((prev) => {
      const isSelected = prev.selectedPresets.includes(prompt);
      const updatedPresets = isSelected
        ? prev.selectedPresets.filter((p) => p !== prompt)
        : [...prev.selectedPresets, prompt];
      return { ...prev, selectedPresets: updatedPresets };
    });
  };

  const removeSelectedPreset = (prompt) => {
    setForm((prev) => ({
      ...prev,
      selectedPresets: prev.selectedPresets.filter((p) => p !== prompt),
    }));
  };

  const handleAllergyAdd = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      const allergy = e.target.value.trim();
      setForm((prev) => {
        if (prev.dietaryRestrictions.allergies.includes(allergy)) {
          return prev;
        }
        return {
          ...prev,
          dietaryRestrictions: {
            ...prev.dietaryRestrictions,
            allergies: [...prev.dietaryRestrictions.allergies, allergy],
          },
        };
      });
      e.target.value = "";
    }
  };

  const handleAllergyRemove = (allergy) => {
    setForm((prev) => ({
      ...prev,
      dietaryRestrictions: {
        ...prev.dietaryRestrictions,
        allergies: prev.dietaryRestrictions.allergies.filter(
          (a) => a !== allergy
        ),
      },
    }));
  };

  const handleDietTypeSelect = (val) => {
    setForm((prev) => ({
      ...prev,
      dietaryRestrictions: {
        ...prev.dietaryRestrictions,
        dietType: val,
      },
    }));
  };

  const filteredCuisines = cuisineSearch
    ? CUISINE_OPTIONS.filter((cuisine) =>
        cuisine._id.toLowerCase().includes(cuisineSearch.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        cuisineDropdownRef.current &&
        !cuisineDropdownRef.current.contains(event.target)
      ) {
        setShowCuisineSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCuisineSearchChange = (e) => {
    const value = e.target.value;
    setCuisineSearch(value);
    setShowCuisineSuggestions(value.length > 0 && filteredCuisines.length > 0);
  };

  const handleCuisineSelect = (cuisineName) => {
    setForm((prev) => {
      if (prev.foodPreferences.lovedCuisines.includes(cuisineName)) {
        return prev;
      }
      return {
        ...prev,
        foodPreferences: {
          ...prev.foodPreferences,
          lovedCuisines: [...prev.foodPreferences.lovedCuisines, cuisineName],
        },
      };
    });
    setCuisineSearch("");
    setShowCuisineSuggestions(false);
  };

  const handleCuisineKeyDown = (e) => {
    if (e.key === "Enter" && cuisineSearch.trim()) {
      e.preventDefault();
      let cuisineToAdd = null;
      
      if (filteredCuisines.length > 0) {
        cuisineToAdd = filteredCuisines[0]._id;
      } else {
        const trimmedSearch = cuisineSearch.trim();
        const matchingCuisine = CUISINE_OPTIONS.find(
          (cuisine) => cuisine._id.toLowerCase() === trimmedSearch.toLowerCase()
        );
        
        if (matchingCuisine) {
          cuisineToAdd = matchingCuisine._id;
        } else {
          return;
        }
      }
      
      handleCuisineSelect(cuisineToAdd);
    } else if (e.key === "Escape") {
      setShowCuisineSuggestions(false);
    }
  };

  const handleCuisineRemove = (cuisine) => {
    setForm((prev) => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        lovedCuisines: prev.foodPreferences.lovedCuisines.filter(
          (c) => c !== cuisine
        ),
      },
    }));
  };

  const handleDislikedIngredientAdd = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      const ingredient = e.target.value.trim();
      setForm((prev) => {
        if (prev.foodPreferences.dislikedIngredients.includes(ingredient)) {
          return prev;
        }
        return {
          ...prev,
          foodPreferences: {
            ...prev.foodPreferences,
            dislikedIngredients: [
              ...prev.foodPreferences.dislikedIngredients,
              ingredient,
            ],
          },
        };
      });
      e.target.value = "";
    }
  };

  const handleDislikedIngredientRemove = (ingredient) => {
    setForm((prev) => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        dislikedIngredients: prev.foodPreferences.dislikedIngredients.filter(
          (i) => i !== ingredient
        ),
      },
    }));
  };

  const handleRegenerate = () => {
    if (mealActions.length === 0) {
      alert(t("editMealPlan.regenerateMealPlanModal.selectMealActionAlert"));
      return;
    }

    setIsLoading(true);

    const combinedPreferences = [extraPreferences?.trim(), ...selectedPresets]
      .filter(Boolean)
      .join(". ");

    const body = {
      mealPlanId,
      weekIndex: selectedWeekIndex,
      dayIndex,
      mealActions,
      targetCalories: Number(calories || 0),
      targetMacros: {
        proteinPercentage: Number(proteinsPercent || 0),
        carbsPercentage: Number(carbsPercent || 0),
        fatsPercentage: Number(fatsPercent || 0),
      },
      dietaryRestrictions: {
        allergies: dietaryRestrictions.allergies || [],
        dietType: dietaryRestrictions.dietType || "Normal",
      },
      foodPreferences: {
        lovedCuisines: foodPreferences.lovedCuisines || [],
        dislikedIngredients: foodPreferences.dislikedIngredients || [],
      },
      effortLevel: Number(effortLevel || 3),
      extraPreferences: combinedPreferences,
      proxyToken: athlete || "",
    };

    submit(`/actions/meal-planner/regenerate-meal-plan`, {
      method: "POST",
      body,
    });
  };

  console.log("form", form);


  return (
    <Modal
      title={t("editMealPlan.regenerateMealPlanModal.title")}
      className="regenerate-meal-plan-modal"
    >
      <div className="body Create-Nutrition-Plan">
        <div className="section-container">
          <h3 className="section-heading">{t("editMealPlan.regenerateMealPlanModal.selectWeekAndDay")}</h3>
          <div className="section-grid">
            <div className="form-col">
              <label className="label" htmlFor="regenerate-week">
                {t("editMealPlan.regenerateMealPlanModal.week")}
              </label>
              <Dropdown
                summaryText={weekOptions[selectedWeekIndex]?.text || "Week 1"}
                options={weekOptions}
                onSelectFn={handleWeekSelect}
                fullOption={false}
                id="regenerate-week"
                summaryStyles={{
                  minHeight: "3rem",
                  padding: "0 12px",
                }}
              />
            </div>
            <div className="form-col">
              <label className="label" htmlFor="regenerate-day">
                {t("editMealPlan.regenerateMealPlanModal.day")}
              </label>
              <Dropdown
                summaryText={dayOptions[dayIndex]?.text || "Day 1"}
                options={dayOptions}
                onSelectFn={handleDaySelect}
                fullOption={false}
                id="regenerate-day"
                summaryStyles={{
                  minHeight: "3rem",
                  padding: "0 12px",
                }}
              />
            </div>
          </div>
        </div>

        <div className="section-container">
          <h3 className="section-heading">{t("editMealPlan.regenerateMealPlanModal.selectMealsToRegenerate")}</h3>
          <div className="meals-list">
            {meals.map((meal, index) => {
              const hasFoods =
                Array.isArray(meal.foods) && meal.foods.length > 0;
              const action = getMealAction(index);

              return (
                <div key={`meal-${index}`} className="meal-action-item">
                  <div className="meal-info">
                    <span className="meal-name">{meal.type}</span>
                    {hasFoods && (
                      <span className="meal-foods-count">
                        {meal.foods.length} {meal.foods.length !== 1 ? t("editMealPlan.regenerateMealPlanModal.foods") : t("editMealPlan.regenerateMealPlanModal.food")}
                      </span>
                    )}
                  </div>
                  <div className="meal-actions">
                    <button
                      type="button"
                      className={`action-button ${
                        action === "like" ? "active" : ""
                      }`}
                      onClick={() => handleMealAction(index, "like")}
                      title={t("editMealPlan.regenerateMealPlanModal.likeTitle")}
                    >
                      <SvgIcon
                        src="/img/new-icons/thumb-up.svg"
                        alt={t("editMealPlan.regenerateMealPlanModal.like")}
                        classNames="w-4! h-4!"
                      />
                      <span>{t("editMealPlan.regenerateMealPlanModal.like")}</span>
                    </button>
                    <button
                      type="button"
                      className={`action-button ${
                        action === "dislike" ? "active" : ""
                      }`}
                      onClick={() => handleMealAction(index, "dislike")}
                      title={t("editMealPlan.regenerateMealPlanModal.dislikeTitle")}
                    >
                      <SvgIcon
                        src="/img/new-icons/thumb-down.svg"
                        alt={t("editMealPlan.regenerateMealPlanModal.dislike")}
                        classNames="w-4! h-4!"
                      />
                      <span>{t("editMealPlan.regenerateMealPlanModal.dislike")}</span>
                    </button>
                    <button
                      type="button"
                      className={`action-button ${
                        action === "remove" ? "active" : ""
                      }`}
                      onClick={() => handleMealAction(index, "remove")}
                      title={t("editMealPlan.regenerateMealPlanModal.removeTitle")}
                    >
                      <SvgIcon
                        src="/img/new-icons/vuesax-linear-trash.svg"
                        alt={t("editMealPlan.regenerateMealPlanModal.remove")}
                        classNames="w-4! h-4!"
                      />
                      <span>{t("editMealPlan.regenerateMealPlanModal.remove")}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="nutrition-plan-macros-container">
          <div className="nutrition-plan-day-title">
            <p>{t("editMealPlan.regenerateMealPlanModal.targetMacros")}</p>
          </div>

          <div className="macros-container-row">
            <div className="macros-column">
              <KcalInputs
                calories={calories}
                proteins={proteins}
                proteinsPercent={proteinsPercent}
                carbs={carbs}
                carbsPercent={carbsPercent}
                fats={fats}
                fibers={fibers}
                fatsPercent={fatsPercent}
                handleChange={handleChange}
              />
            </div>

            <div className="macros-column">
              <NutrientsChart
                providedId={"nutrient-chart-regenerate-meal-plan"}
                dayStats={{ nutrients: { proteins, carbs, fats } }}
                width={250}
              />
            </div>
          </div>
        </div>

        <div className="section-divider"></div>

        <div className="basis-details-section">
          <h3 className="section-label">{t("editMealPlan.regenerateMealPlanModal.basisDetails")}</h3>
          <div className="basis-details-content">
            <div className="basis-details-row">
              <div className="basis-details-column">
                <div className="form-col">
                  <label className="label" htmlFor="regenerate-effort-level">
                    <span>{t("editMealPlan.regenerateMealPlanModal.cookingEffortLevel")}</span>
                    {/* <ToolTip
                      id="effort-level-hint"
                      body="Cooking effort level (1-5): 1-2=Easy, 3=Medium, 4-5=Hard"
                    >
                      <SvgIcon src="/img/new-icons/info-circle.svg" alt="info" />
                    </ToolTip> */}
                  </label>
                  <div className="effort-slider-outer">
                    <div className="effort-slider-inner">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <button
                          key={level}
                          type="button"
                          className={`effort-dot ${Number(effortLevel) === level ? "active" : ""}`}
                          onClick={() => handleChange({ target: { name: "effortLevel", value: level } })}
                        />
                      ))}
                    </div>
                    <div className="effort-labels">
                      <span className={Number(effortLevel) <= 2 ? "active" : ""}>{t("editMealPlan.regenerateMealPlanModal.easy")}</span>
                      <span className={Number(effortLevel) === 3 ? "active" : ""}>{t("editMealPlan.regenerateMealPlanModal.medium")}</span>
                      <span className={Number(effortLevel) >= 4 ? "active" : ""}>{t("editMealPlan.regenerateMealPlanModal.hard")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="section-divider"></div>

        <div className="basis-details-section">
          <h3 className="section-label">{t("editMealPlan.regenerateMealPlanModal.dietaryRestrictions")}</h3>
          <div className="basis-details-content">
            <div className="basis-details-row">
            <div className="form-col">
              <label className="label" htmlFor="regenerate-diet-type">
                {t("editMealPlan.regenerateMealPlanModal.dietType")}
              </label>
              <div className="diet-type-options">
                <label className="diet-type-option">
                  <input
                    type="radio"
                    name="dietType"
                    value=""
                    checked={dietaryRestrictions.dietType === ""}
                    onChange={(e) => handleDietTypeSelect(e.target.value)}
                  />
                  <span>{t("editMealPlan.regenerateMealPlanModal.none")}</span>
                </label>
                {DIETARY_TYPES.slice(0, 3).map((diet) => (
                  <label key={diet.value} className="diet-type-option">
                    <input
                      type="radio"
                      name="dietType"
                      value={diet.value}
                      checked={dietaryRestrictions.dietType === diet.value}
                      onChange={(e) => handleDietTypeSelect(e.target.value)}
                    />
                    <span>
                      {t(`dietaryTypes.${DIETARY_I18N[diet.value]}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-col">
              <label className="label" htmlFor="regenerate-allergies">
                {t("editMealPlan.regenerateMealPlanModal.allergies")}
              </label>
              <div className="allergies-textarea-wrapper">
                {dietaryRestrictions.allergies.length > 0 && (
                  <div className="allergies-tags">
                    {dietaryRestrictions.allergies.map((allergy, index) => (
                      <span key={index} className="allergy-tag">
                        {allergy}
                        <button
                          type="button"
                          onClick={() => handleAllergyRemove(allergy)}
                          className="allergy-tag-remove"
                          title={t("editMealPlan.regenerateMealPlanModal.removeAllergy")}
                        >
                          <img
                            className="remove-icon"
                            src="/img/new-icons/red-cross.svg"
                            alt="remove"
                          />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <textarea
                  id="regenerate-allergies"
                  placeholder=" "
                  onKeyDown={handleAllergyAdd}
                  className="allergies-textarea"
                  rows={3}
                />
                <span className="allergies-custom-placeholder">{t("editMealPlan.regenerateMealPlanModal.pressEnterToAdd")}</span>
              </div>
            </div>
            </div>
          </div>
        </div>

        <div className="section-divider"></div>

        <div className="basis-details-section">
          <h3 className="section-label">{t("editMealPlan.regenerateMealPlanModal.foodPreferences")}</h3>
          <div className="basis-details-content">
            <div className="basis-details-row">
            <div className="form-col">
              <label className="label" htmlFor="regenerate-loved-cuisines">
                {t("editMealPlan.regenerateMealPlanModal.lovedCuisines")}
              </label>
              <div className="loved-cuisines-wrapper" ref={cuisineDropdownRef}>
                {foodPreferences.lovedCuisines.length > 0 && (
                  <div className="loved-cuisines-tags">
                    {foodPreferences.lovedCuisines.map((cuisine, index) => (
                      <span key={index} className="loved-cuisine-tag">
                        {cuisine}
                        <button
                          type="button"
                          onClick={() => handleCuisineRemove(cuisine)}
                          className="loved-cuisine-tag-remove"
                          title={t("editMealPlan.regenerateMealPlanModal.removeCuisine")}
                        >
                          <img
                            className="remove-icon"
                            src="/img/new-icons/red-cross.svg"
                            alt="remove"
                          />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <textarea
                  ref={cuisineInputRef}
                  id="regenerate-loved-cuisines"
                  placeholder=" "
                  value={cuisineSearch}
                  onChange={handleCuisineSearchChange}
                  onKeyDown={handleCuisineKeyDown}
                  onFocus={() => {
                    if (cuisineSearch && filteredCuisines.length > 0) {
                      setShowCuisineSuggestions(true);
                    }
                  }}
                  className="loved-cuisines-textarea"
                  rows={6}
                />
                <span className="loved-cuisines-custom-placeholder">
                  {t("editMealPlan.regenerateMealPlanModal.pressEnterToAdd")}
                </span>
                {showCuisineSuggestions && filteredCuisines.length > 0 && (
                  <ul className="cuisine-suggestions">
                    {filteredCuisines.slice(0, 10).map((cuisine, index) => (
                      <li
                        key={cuisine._id}
                        onClick={() => handleCuisineSelect(cuisine._id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCuisineSelect(cuisine._id);
                          }
                        }}
                        tabIndex={0}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          color: "#fff",
                          backgroundColor:
                            index === 0 ? "#333" : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#333";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor =
                            index === 0 ? "#333" : "transparent";
                        }}
                      >
                        {cuisine._id}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="form-col">
              <label
                className="label"
                htmlFor="regenerate-disliked-ingredients"
              >
                {t("editMealPlan.regenerateMealPlanModal.dislikedIngredients")}
              </label>
              <div className="disliked-ingredients-textarea-wrapper">
                {foodPreferences.dislikedIngredients.length > 0 && (
                  <div className="disliked-ingredients-tags">
                    {foodPreferences.dislikedIngredients.map(
                      (ingredient, index) => (
                        <span key={index} className="disliked-ingredient-tag">
                          {ingredient}
                          <button
                            type="button"
                            onClick={() =>
                              handleDislikedIngredientRemove(ingredient)
                            }
                            className="disliked-ingredient-tag-remove"
                            title={t("editMealPlan.regenerateMealPlanModal.removeIngredient")}
                          >
                            <img
                              className="remove-icon"
                              src="/img/new-icons/red-cross.svg"
                              alt="remove"
                            />
                          </button>
                        </span>
                      )
                    )}
                  </div>
                )}
                <textarea
                  id="regenerate-disliked-ingredients"
                  placeholder=" "
                  onKeyDown={handleDislikedIngredientAdd}
                  className="disliked-ingredients-textarea"
                  rows={6}
                />
                <span className="disliked-ingredients-custom-placeholder">
                  {t("editMealPlan.regenerateMealPlanModal.pressEnterToAdd")}
                </span>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* <ExtraPreferences
          extraPreferences={extraPreferences}
          selectedPresets={selectedPresets}
          presetPrompts={presetPrompts}
          handleChange={handleChange}
          removeSelectedPreset={removeSelectedPreset}
          handlePresetPrompt={handlePresetPrompt}
        /> */}
      </div>

      <footer>
        <a
          role="button"
          data-button-type="secondary"
          data-action="cancel"
          href="/actions/refresh?modal=&sourceOwner=&week="
        >
          {t("editMealPlan.regenerateMealPlanModal.cancel")}
        </a>
        <button
          type="button"
          data-button-type="primary"
          onClick={handleRegenerate}
          disabled={isLoading || mealActions.length === 0}
        >
          {isLoading ? t("editMealPlan.regenerateMealPlanModal.regenerating") : t("editMealPlan.regenerateMealPlanModal.regenerateMeals")}
        </button>
      </footer>
    </Modal>
  );
};

RegenerateMealPlanModal.propTypes = {
  locals: PropTypes.object,
};

export default RegenerateMealPlanModal;
